import { useEffect, useMemo, useRef } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useAuthor } from '@/hooks/useAuthor';
import { useMyPosts } from '@/hooks/useMyPosts';
import { ProfileHeader } from '@/components/ProfileHeader';
import { RetroNoteCard } from '@/components/RetroNoteCard';
import { RetroNoteSkeleton } from '@/components/RetroNoteSkeleton';
import { genUserName } from '@/lib/genUserName';

// Hardcoded profile: npub1hee433872q2gen90cqh2ypwcq9z7y5ugn23etrd2l2rrwpruss8qwmrsv6
const PROFILE_PUBKEY = 'be7358c4fe50148cccafc02ea205d80145e253889aa3958daafa8637047c840e';

const Index = () => {
  const pubkey = PROFILE_PUBKEY;

  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = useMyPosts(pubkey);

  const events = useMemo(
    () => data?.pages.flatMap((p) => p.events) ?? [],
    [data],
  );

  const displayName = metadata?.display_name ?? metadata?.name ?? genUserName(pubkey);

  useSeoMeta({
    title: `${displayName} // PROFILE.EXE`,
    description: 'A retro 90s-themed Nostr profile page.',
  });

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!hasNextPage || isFetchingNextPage) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: '400px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, events.length]);

  return (
    <main className="relative min-h-screen">
      <TopBar />

      <div className="max-w-3xl mx-auto px-3 md:px-4 pt-4 md:pt-6 pb-24 space-y-6">
        <ProfileHeader
          pubkey={pubkey}
          metadata={metadata}
          postCount={events.length}
          isLoading={isLoading}
        />

        <FeedHeader count={events.length} loading={isLoading} />

        {isError && (
          <div className="pixel-border bg-black/80 p-4 font-pixel text-neon-red text-sm">
            <div className="mb-2">&gt; FATAL ERROR</div>
            <div className="font-mono text-xs text-foreground/80 break-all">
              {error instanceof Error ? error.message : 'Unknown error'}
            </div>
            <button
              onClick={() => refetch()}
              className="mt-3 pixel-border-soft px-3 py-1 text-neon-cyan hover:text-glow-cyan"
            >
              [RETRY]
            </button>
          </div>
        )}

        {isLoading && (
          <div className="space-y-4">
            <RetroNoteSkeleton />
            <RetroNoteSkeleton />
            <RetroNoteSkeleton />
          </div>
        )}

        {!isLoading && events.length === 0 && !isError && (
          <EmptyState />
        )}

        <div className="space-y-4">
          {events.map((event, i) => (
            <RetroNoteCard key={event.id} event={event} index={i} />
          ))}
        </div>

        {/* Infinite scroll footer */}
        {events.length > 0 && (
          <div className="pt-4 text-center">
            {isFetchingNextPage ? (
              <div className="font-pixel text-xs text-neon-amber text-glow-amber animate-pulse tracking-widest">
                LOADING NEXT LEVEL…
              </div>
            ) : hasNextPage ? (
              <button
                onClick={() => fetchNextPage()}
                className="pixel-border-soft bg-black/80 px-5 py-2 font-pixel text-xs text-neon-cyan hover:text-glow-cyan tracking-widest"
              >
                &gt; PRESS START TO CONTINUE
              </button>
            ) : (
              <div className="font-pixel text-xs text-muted-foreground tracking-widest">
                ★ GAME OVER — NO MORE POSTS ★
              </div>
            )}
            <div ref={sentinelRef} className="h-1 w-full" aria-hidden />
          </div>
        )}
      </div>
    </main>
  );
};

function TopBar() {
  return (
    <div className="sticky top-0 z-30 border-b-2 border-primary/70 bg-black/80 backdrop-blur-md">
      <div className="max-w-3xl mx-auto px-3 md:px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-pixel text-neon-green text-glow-green animate-star-twinkle">★</span>
          <span className="font-pixel text-sm md:text-base text-neon-magenta text-glow-magenta tracking-widest truncate">
            PROFILE.EXE
          </span>
          <span className="hidden sm:inline font-pixel text-[10px] text-muted-foreground tracking-widest">
            v1.99
          </span>
        </div>
      </div>
      <Marquee />
    </div>
  );
}

function Marquee() {
  const text = '★ WELCOME TO THE NOSTRWAVE ZONE ★ YOUR POSTS IMMORTALIZED ON THE PERMANENT WEB ★ NO CENTRAL SERVER ★ NO LOSERS ALLOWED ★ ';
  return (
    <div className="overflow-hidden border-t border-primary/30 bg-black/60">
      <div className="whitespace-nowrap font-pixel text-[10px] text-neon-cyan/80 py-1 animate-marquee inline-block">
        <span className="px-2">{text}</span>
        <span className="px-2">{text}</span>
      </div>
    </div>
  );
}

function FeedHeader({ count, loading }: { count: number; loading: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="font-pixel text-lg md:text-xl text-neon-cyan text-glow-cyan tracking-widest blink-cursor">
        &gt; TRANSMISSIONS
      </h2>
      <div className="font-pixel text-[10px] text-muted-foreground tracking-widest">
        {loading ? 'SCANNING…' : `LOADED:${String(count).padStart(3, '0')}`}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="pixel-border-soft bg-black/60 p-8 text-center space-y-3">
      <div className="font-pixel text-4xl text-neon-amber text-glow-amber animate-star-twinkle">?</div>
      <div className="font-pixel text-sm text-neon-cyan tracking-widest">NO TRANSMISSIONS DETECTED</div>
      <div className="font-mono text-xs text-muted-foreground max-w-sm mx-auto">
        Either this player has not broadcast any messages yet, or the relays are still syncing.
      </div>
    </div>
  );
}

export default Index;
